import type { DashboardMetricItemData } from '../../../../types/dashboardMetrics.types'
import type { DashboardPieChartSlice } from '../MetricCharts/DashboardPieChart.component'
import type { RangeKey } from '@dataTypes/dashboard.types'

const RANGE_COMPARISON: Record<RangeKey, string> = {
  '1D': 'vs. yesterday',
  '1W': 'vs. last week',
  '1M': 'vs. last month',
  '1Y': 'vs. last year',
  YTD: 'vs. last YTD',
}

function footerFor(range: RangeKey, pct = '6%', amt = '$800'): string {
  return `${pct} (${amt}) ${RANGE_COMPARISON[range]}`
}

// ---------------------------------------------------------------------------
// Patients age chart
// ---------------------------------------------------------------------------

const PATIENTS_AGE_VALUES: Record<
  RangeKey,
  Array<{ label: string; Male: number; Female: number }>
> = {
  '1D': [
    { label: '0-17', Male: 2, Female: 2 },
    { label: '18-24', Male: 3, Female: 4 },
    { label: '25-34', Male: 5, Female: 5 },
    { label: '35-44', Male: 6, Female: 6 },
    { label: '45-54', Male: 5, Female: 5 },
    { label: '55-64', Male: 4, Female: 4 },
    { label: '65+', Male: 3, Female: 3 },
  ],
  '1W': [
    { label: '0-17', Male: 11, Female: 12 },
    { label: '18-24', Male: 15, Female: 16 },
    { label: '25-34', Male: 22, Female: 22 },
    { label: '35-44', Male: 29, Female: 27 },
    { label: '45-54', Male: 25, Female: 24 },
    { label: '55-64', Male: 19, Female: 19 },
    { label: '65+', Male: 14, Female: 14 },
  ],
  '1M': [
    { label: '0-17', Male: 22, Female: 23 },
    { label: '18-24', Male: 30, Female: 32 },
    { label: '25-34', Male: 44, Female: 44 },
    { label: '35-44', Male: 58, Female: 54 },
    { label: '45-54', Male: 50, Female: 48 },
    { label: '55-64', Male: 38, Female: 37 },
    { label: '65+', Male: 28, Female: 27 },
  ],
  '1Y': [
    { label: '0-17', Male: 264, Female: 276 },
    { label: '18-24', Male: 360, Female: 384 },
    { label: '25-34', Male: 528, Female: 528 },
    { label: '35-44', Male: 696, Female: 648 },
    { label: '45-54', Male: 600, Female: 576 },
    { label: '55-64', Male: 456, Female: 444 },
    { label: '65+', Male: 336, Female: 324 },
  ],
  YTD: [
    { label: '0-17', Male: 66, Female: 69 },
    { label: '18-24', Male: 90, Female: 96 },
    { label: '25-34', Male: 132, Female: 132 },
    { label: '35-44', Male: 174, Female: 162 },
    { label: '45-54', Male: 150, Female: 144 },
    { label: '55-64', Male: 114, Female: 111 },
    { label: '65+', Male: 84, Female: 81 },
  ],
}

export function getPatientsAgeData(
  range: RangeKey = '1D',
): Array<{ label: string; Male: number; Female: number }> {
  return PATIENTS_AGE_VALUES[range]
}

export const PATIENTS_AGE_SERIES = [
  { dataKey: 'Male', name: 'Male', color: 'var(--blue-400)' },
  { dataKey: 'Female', name: 'Female', color: 'var(--orange-300)' },
]

type AxisConfig = { yAxisDomain: [number, number]; yAxisTicks: number[] }

const PATIENTS_AGE_AXIS: Record<RangeKey, AxisConfig> = {
  '1D': { yAxisDomain: [0, 10], yAxisTicks: [0, 2, 4, 6, 8, 10] },
  '1W': { yAxisDomain: [0, 40], yAxisTicks: [0, 10, 20, 30, 40] },
  '1M': { yAxisDomain: [0, 80], yAxisTicks: [0, 20, 40, 60, 80] },
  '1Y': { yAxisDomain: [0, 800], yAxisTicks: [0, 200, 400, 600, 800] },
  YTD: { yAxisDomain: [0, 200], yAxisTicks: [0, 50, 100, 150, 200] },
}

export function getPatientsAgeAxisConfig(range: RangeKey = '1D'): AxisConfig {
  return PATIENTS_AGE_AXIS[range]
}

// ---------------------------------------------------------------------------
// Patient status chart
// ---------------------------------------------------------------------------

const PATIENT_STATUS_VALUES: Record<
  RangeKey,
  Array<{ label: string; value: number }>
> = {
  '1D': [
    { label: 'Active', value: 12 },
    { label: 'Inactive', value: 4 },
    { label: 'New patients', value: 3 },
    { label: 'Deceased', value: 2 },
    { label: 'Blocked', value: 1 },
  ],
  '1W': [
    { label: 'Active', value: 60 },
    { label: 'Inactive', value: 20 },
    { label: 'New patients', value: 14 },
    { label: 'Deceased', value: 1 },
    { label: 'Blocked', value: 6 },
  ],
  '1M': [
    { label: 'Active', value: 120 },
    { label: 'Inactive', value: 45 },
    { label: 'New patients', value: 28 },
    { label: 'Deceased', value: 5 },
    { label: 'Blocked', value: 12 },
  ],
  '1Y': [
    { label: 'Active', value: 1440 },
    { label: 'Inactive', value: 540 },
    { label: 'New patients', value: 336 },
    { label: 'Deceased', value: 60 },
    { label: 'Blocked', value: 144 },
  ],
  YTD: [
    { label: 'Active', value: 360 },
    { label: 'Inactive', value: 135 },
    { label: 'New patients', value: 84 },
    { label: 'Deceased', value: 15 },
    { label: 'Blocked', value: 36 },
  ],
}

/** X-axis / legend order per Figma (Patient demographics & status). */
const PATIENT_STATUS_ORDER = [
  'Active',
  'Inactive',
  'New patients',
  'Deceased',
  'Blocked',
] as const

export const PATIENT_STATUS_LEGEND_ORDER = [...PATIENT_STATUS_ORDER]

export function getPatientStatusData(
  range: RangeKey = '1D',
): Array<{ label: string; value: number }> {
  const rows = PATIENT_STATUS_VALUES[range]
  const byLabel = new Map(rows.map((row) => [row.label, row]))
  return PATIENT_STATUS_ORDER.map((label) => {
    const row = byLabel.get(label)
    return row ?? { label, value: 0 }
  })
}

const PATIENT_STATUS_AXIS: Record<RangeKey, AxisConfig> = {
  '1D': { yAxisDomain: [0, 25], yAxisTicks: [0, 5, 10, 15, 20, 25] },
  '1W': { yAxisDomain: [0, 80], yAxisTicks: [0, 20, 40, 60, 80] },
  '1M': { yAxisDomain: [0, 150], yAxisTicks: [0, 50, 100, 150] },
  '1Y': { yAxisDomain: [0, 1600], yAxisTicks: [0, 400, 800, 1200, 1600] },
  YTD: { yAxisDomain: [0, 400], yAxisTicks: [0, 100, 200, 300, 400] },
}

export function getPatientStatusAxisConfig(range: RangeKey = '1D'): AxisConfig {
  return PATIENT_STATUS_AXIS[range]
}

/** Bar / legend colors per Figma node 5484-133559. */
export const PATIENT_STATUS_COLORS: Record<string, string> = {
  Active: 'var(--blue-700)',
  Inactive: 'var(--blue-600)',
  'New patients': 'var(--blue-400)',
  Deceased: 'var(--blue-300)',
  Blocked: 'var(--orange-300)',
}

// ---------------------------------------------------------------------------
// Schedule overview column
// ---------------------------------------------------------------------------

const SCHEDULE_OVERVIEW_VALUES: Record<
  RangeKey,
  {
    claimsOutstanding: string
    scheduledAppointments: string
    scheduledPatients: string
    scheduledProduction: string
    brokenAppointments: string
    noShow: string
    rescheduleRate: string
  }
> = {
  '1D': {
    claimsOutstanding: '92.4%',
    scheduledAppointments: '120',
    scheduledPatients: '98',
    scheduledProduction: '$62.3K',
    brokenAppointments: '23.4%',
    noShow: '31.4%',
    rescheduleRate: '35%',
  },
  '1W': {
    claimsOutstanding: '90.1%',
    scheduledAppointments: '598',
    scheduledPatients: '490',
    scheduledProduction: '$311K',
    brokenAppointments: '21.0%',
    noShow: '28.0%',
    rescheduleRate: '32%',
  },
  '1M': {
    claimsOutstanding: '89.5%',
    scheduledAppointments: '2,390',
    scheduledPatients: '1,960',
    scheduledProduction: '$1.2M',
    brokenAppointments: '20.0%',
    noShow: '26.0%',
    rescheduleRate: '30%',
  },
  '1Y': {
    claimsOutstanding: '88.2%',
    scheduledAppointments: '28,680',
    scheduledPatients: '23,520',
    scheduledProduction: '$14.9M',
    brokenAppointments: '18.0%',
    noShow: '24.0%',
    rescheduleRate: '28%',
  },
  YTD: {
    claimsOutstanding: '89.0%',
    scheduledAppointments: '7,170',
    scheduledPatients: '5,880',
    scheduledProduction: '$3.7M',
    brokenAppointments: '19.0%',
    noShow: '25.0%',
    rescheduleRate: '29%',
  },
}

export function getOperationsScheduleOverviewColumnMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = SCHEDULE_OVERVIEW_VALUES[range]
  return [
    {
      id: 'claims-outstanding',
      leftLabel: 'Claims outstanding',
      value: v.claimsOutstanding,
    },
    {
      id: 'scheduled-appointments',
      leftLabel: 'Scheduled appointments',
      value: v.scheduledAppointments,
    },
    {
      id: 'scheduled-patients',
      leftLabel: 'Scheduled patients',
      value: v.scheduledPatients,
    },
    {
      id: 'scheduled-production',
      leftLabel: 'Scheduled production',
      value: v.scheduledProduction,
    },
    {
      id: 'broken-appointments',
      leftLabel: 'Broken appointments',
      value: v.brokenAppointments,
    },
    { id: 'no-show', leftLabel: 'No-show', value: v.noShow },
    {
      id: 'reschedule-rate',
      leftLabel: 'Reschedule rate',
      value: v.rescheduleRate,
    },
  ]
}

// ---------------------------------------------------------------------------
// Operatory efficiency column
// ---------------------------------------------------------------------------

const OPERATORY_EFFICIENCY_VALUES: Record<
  RangeKey,
  {
    scheduleUtilization: string
    openHours: string
    totalWorkedDays: string
    missingProcedureCodes: string
    unassnProcedures: string
    schedulesWoCode: string
  }
> = {
  '1D': {
    scheduleUtilization: '92.4%',
    openHours: '8 AM - 5 PM',
    totalWorkedDays: '1 day',
    missingProcedureCodes: '6',
    unassnProcedures: '3',
    schedulesWoCode: '8',
  },
  '1W': {
    scheduleUtilization: '90.8%',
    openHours: '8 AM - 5 PM',
    totalWorkedDays: '5 days',
    missingProcedureCodes: '30',
    unassnProcedures: '15',
    schedulesWoCode: '40',
  },
  '1M': {
    scheduleUtilization: '89.5%',
    openHours: '8 AM - 5 PM',
    totalWorkedDays: '22 days',
    missingProcedureCodes: '120',
    unassnProcedures: '60',
    schedulesWoCode: '160',
  },
  '1Y': {
    scheduleUtilization: '88.2%',
    openHours: '8 AM - 5 PM',
    totalWorkedDays: '264 days',
    missingProcedureCodes: '1,440',
    unassnProcedures: '720',
    schedulesWoCode: '1,920',
  },
  YTD: {
    scheduleUtilization: '89.0%',
    openHours: '8 AM - 5 PM',
    totalWorkedDays: '66 days',
    missingProcedureCodes: '360',
    unassnProcedures: '180',
    schedulesWoCode: '480',
  },
}

export function getOperationsOperatoryEfficiencyColumnMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = OPERATORY_EFFICIENCY_VALUES[range]
  return [
    {
      id: 'schedule-utilization',
      leftLabel: 'Schedule utilization',
      value: v.scheduleUtilization,
    },
    { id: 'open-hours', leftLabel: 'Open hours', value: v.openHours },
    {
      id: 'total-worked-days',
      leftLabel: 'Total worked days',
      value: v.totalWorkedDays,
    },
    {
      id: 'missing-procedure-codes',
      leftLabel: 'Missing procedure codes',
      value: v.missingProcedureCodes,
    },
    {
      id: 'unassn-procedures',
      leftLabel: 'Unassigned procedures',
      value: v.unassnProcedures,
    },
    {
      id: 'schedules-wo-code',
      leftLabel: 'Schedules w/o code',
      value: v.schedulesWoCode,
    },
  ]
}

// ---------------------------------------------------------------------------
// Schedule operations card (Operations tab) — disruptions + capacity focus
// ---------------------------------------------------------------------------

const SCHEDULE_DISRUPTIONS_VALUES: Record<
  RangeKey,
  {
    brokenAppointments: string
    noShow: string
    rescheduleRate: string
    sameDayCancellations: string
    appointmentOverruns: string
    lostChairTime: string
  }
> = {
  '1D': {
    brokenAppointments: '23.4%',
    noShow: '3',
    rescheduleRate: '35%',
    sameDayCancellations: '4',
    appointmentOverruns: '17',
    lostChairTime: '4.5 hrs',
  },
  '1W': {
    brokenAppointments: '21.0%',
    noShow: '14',
    rescheduleRate: '32%',
    sameDayCancellations: '18',
    appointmentOverruns: '82',
    lostChairTime: '22 hrs',
  },
  '1M': {
    brokenAppointments: '20.0%',
    noShow: '58',
    rescheduleRate: '30%',
    sameDayCancellations: '76',
    appointmentOverruns: '340',
    lostChairTime: '92 hrs',
  },
  '1Y': {
    brokenAppointments: '18.0%',
    noShow: '692',
    rescheduleRate: '28%',
    sameDayCancellations: '907',
    appointmentOverruns: '4,080',
    lostChairTime: '1,104 hrs',
  },
  YTD: {
    brokenAppointments: '19.0%',
    noShow: '173',
    rescheduleRate: '29%',
    sameDayCancellations: '227',
    appointmentOverruns: '1,020',
    lostChairTime: '276 hrs',
  },
}

const CAPACITY_OPENINGS_VALUES: Record<
  RangeKey,
  {
    openChairTime: string
    unfilledSlots: string
    waitListPatients: string
    confirmationsPending: string
    newPatientOpenings: string
    hygieneOpenings: string
  }
> = {
  '1D': {
    openChairTime: '3 hrs',
    unfilledSlots: '6',
    waitListPatients: '12',
    confirmationsPending: '8',
    newPatientOpenings: '2',
    hygieneOpenings: '3',
  },
  '1W': {
    openChairTime: '15 hrs',
    unfilledSlots: '28',
    waitListPatients: '54',
    confirmationsPending: '36',
    newPatientOpenings: '9',
    hygieneOpenings: '14',
  },
  '1M': {
    openChairTime: '63 hrs',
    unfilledSlots: '118',
    waitListPatients: '226',
    confirmationsPending: '152',
    newPatientOpenings: '38',
    hygieneOpenings: '59',
  },
  '1Y': {
    openChairTime: '756 hrs',
    unfilledSlots: '1,416',
    waitListPatients: '2,712',
    confirmationsPending: '1,824',
    newPatientOpenings: '456',
    hygieneOpenings: '708',
  },
  YTD: {
    openChairTime: '189 hrs',
    unfilledSlots: '354',
    waitListPatients: '678',
    confirmationsPending: '456',
    newPatientOpenings: '114',
    hygieneOpenings: '177',
  },
}

export function getOperationsScheduleDisruptionsMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = SCHEDULE_DISRUPTIONS_VALUES[range]
  return [
    {
      id: 'ops-broken-appointments',
      leftLabel: 'Broken appointments',
      value: v.brokenAppointments,
    },
    { id: 'ops-no-show', leftLabel: 'No-show', value: v.noShow },
    {
      id: 'ops-reschedule-rate',
      leftLabel: 'Reschedule rate',
      value: v.rescheduleRate,
    },
    {
      id: 'ops-same-day-cancellations',
      leftLabel: 'Same-day cancellations',
      value: v.sameDayCancellations,
    },
    {
      id: 'ops-appointment-overruns',
      leftLabel: 'Appointment overruns',
      value: v.appointmentOverruns,
    },
    {
      id: 'ops-lost-chair-time',
      leftLabel: 'Lost chair time',
      value: v.lostChairTime,
    },
  ]
}

export function getOperationsCapacityOpeningsMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = CAPACITY_OPENINGS_VALUES[range]
  return [
    {
      id: 'ops-open-chair-time',
      leftLabel: 'Open chair time',
      value: v.openChairTime,
    },
    {
      id: 'ops-unfilled-slots',
      leftLabel: 'Unfilled slots',
      value: v.unfilledSlots,
    },
    {
      id: 'ops-wait-list',
      leftLabel: 'Wait list patients',
      value: v.waitListPatients,
    },
    {
      id: 'ops-confirmations-pending',
      leftLabel: 'Confirmations pending',
      value: v.confirmationsPending,
    },
    {
      id: 'ops-new-patient-openings',
      leftLabel: 'New patient openings',
      value: v.newPatientOpenings,
    },
    {
      id: 'ops-hygiene-openings',
      leftLabel: 'Hygiene openings',
      value: v.hygieneOpenings,
    },
  ]
}

// ---------------------------------------------------------------------------
// Expenses breakdown & Seat utilization donuts
// ---------------------------------------------------------------------------

/** Pie segment colors — Figma 5462-118299 (Expenses breakdown & Seat utilization). */
export const OPERATIONS_EXPENSE_PIE_COLORS: Record<string, string> = {
  Payroll: 'var(--blue-700)',
  Maintenance: 'var(--blue-500)',
  Supplies: 'var(--blue-400)',
  Marketing: 'var(--blue-200)',
  Lab: 'var(--orange-300)',
  'Service fee': 'var(--orange-400)',
}

const OPERATIONS_EXPENSE_PIE_ORDER = [
  'Payroll',
  'Maintenance',
  'Supplies',
  'Marketing',
  'Lab',
  'Service fee',
] as const

function buildOperationsPieSlices(
  values: Record<(typeof OPERATIONS_EXPENSE_PIE_ORDER)[number], number>,
): DashboardPieChartSlice[] {
  return OPERATIONS_EXPENSE_PIE_ORDER.map((name) => ({
    name,
    value: values[name],
    color: OPERATIONS_EXPENSE_PIE_COLORS[name],
  }))
}

export const OPERATIONS_EXPENSE_DONUT_SLICES = buildOperationsPieSlices({
  Payroll: 35,
  Maintenance: 12,
  Supplies: 18,
  Marketing: 15,
  Lab: 10,
  'Service fee': 10,
})

export const OPERATIONS_SEAT_DONUT_SLICES = buildOperationsPieSlices({
  Payroll: 28,
  Maintenance: 32,
  Supplies: 14,
  Marketing: 10,
  Lab: 8,
  'Service fee': 8,
})

const EXPENSES_SUMMARY_VALUES: Record<
  RangeKey,
  {
    totalOverhead: string
    expenseRatio: string
    payrollShare: string
    suppliesSpend: string
  }
> = {
  '1D': {
    totalOverhead: '$24.1K',
    expenseRatio: '29.1%',
    payrollShare: '41%',
    suppliesSpend: '$4.3K',
  },
  '1W': {
    totalOverhead: '$120K',
    expenseRatio: '28.5%',
    payrollShare: '40%',
    suppliesSpend: '$21.6K',
  },
  '1M': {
    totalOverhead: '$480K',
    expenseRatio: '28.0%',
    payrollShare: '39%',
    suppliesSpend: '$86.4K',
  },
  '1Y': {
    totalOverhead: '$5.8M',
    expenseRatio: '27.5%',
    payrollShare: '38%',
    suppliesSpend: '$1.04M',
  },
  YTD: {
    totalOverhead: '$1.4M',
    expenseRatio: '27.8%',
    payrollShare: '39%',
    suppliesSpend: '$252K',
  },
}

export function getOperationsExpensesSummaryMetrics(range: RangeKey = '1D'): {
  totalOverhead: string
  expenseRatio: string
  payrollShare: string
  suppliesSpend: string
} {
  return EXPENSES_SUMMARY_VALUES[range]
}

// Seat utilization uses same donut slices; summary mixes cost + utilization signals
const SEAT_UTILIZATION_VALUES: Record<
  RangeKey,
  {
    totalOverhead: string
    expenseRatio: string
    avgSeatUtilization: string
    peakSeatUtilization: string
  }
> = {
  '1D': {
    totalOverhead: '$18.6K',
    expenseRatio: '22.4%',
    avgSeatUtilization: '76%',
    peakSeatUtilization: '92%',
  },
  '1W': {
    totalOverhead: '$93K',
    expenseRatio: '21.8%',
    avgSeatUtilization: '78%',
    peakSeatUtilization: '95%',
  },
  '1M': {
    totalOverhead: '$372K',
    expenseRatio: '21.2%',
    avgSeatUtilization: '79%',
    peakSeatUtilization: '96%',
  },
  '1Y': {
    totalOverhead: '$4.5M',
    expenseRatio: '20.8%',
    avgSeatUtilization: '77%',
    peakSeatUtilization: '94%',
  },
  YTD: {
    totalOverhead: '$1.1M',
    expenseRatio: '21.0%',
    avgSeatUtilization: '78%',
    peakSeatUtilization: '95%',
  },
}

export function getOperationsSeatUtilizationMetrics(range: RangeKey = '1D'): {
  totalOverhead: string
  expenseRatio: string
  avgSeatUtilization: string
  peakSeatUtilization: string
} {
  return SEAT_UTILIZATION_VALUES[range]
}

// ---------------------------------------------------------------------------
// Hygiene metrics
// ---------------------------------------------------------------------------

const HYGIENE_VALUES: Record<
  RangeKey,
  {
    netHygieneProduction: string
    netGoal: string
    productionPerVisit: string
    visitGoal: string
    reappointmentRate: string
    reappGoal: string
    ar90Goal: string
    arOver90: string
  }
> = {
  '1D': {
    netHygieneProduction: '$8.4K',
    netGoal: '19K',
    productionPerVisit: '$6.2K',
    visitGoal: '19K',
    reappointmentRate: '24%',
    reappGoal: '25%',
    ar90Goal: '25%',
    arOver90: '$8.4K',
  },
  '1W': {
    netHygieneProduction: '$42K',
    netGoal: '95K',
    productionPerVisit: '$6.0K',
    visitGoal: '19K',
    reappointmentRate: '25%',
    reappGoal: '25%',
    ar90Goal: '$12K',
    arOver90: '$8.6K',
  },
  '1M': {
    netHygieneProduction: '$168K',
    netGoal: '190K',
    productionPerVisit: '$5.9K',
    visitGoal: '19K',
    reappointmentRate: '26%',
    reappGoal: '25%',
    ar90Goal: '$48K',
    arOver90: '$9.1K',
  },
  '1Y': {
    netHygieneProduction: '$2.0M',
    netGoal: '2.3M',
    productionPerVisit: '$5.8K',
    visitGoal: '19K',
    reappointmentRate: '27%',
    reappGoal: '25%',
    ar90Goal: '$520K',
    arOver90: '$10.2K',
  },
  YTD: {
    netHygieneProduction: '$504K',
    netGoal: '580K',
    productionPerVisit: '$5.9K',
    visitGoal: '19K',
    reappointmentRate: '26%',
    reappGoal: '25%',
    ar90Goal: '$156K',
    arOver90: '$9.4K',
  },
}

export function getOperationsHygieneMetrics(
  range: RangeKey = '1D',
): DashboardMetricItemData[] {
  const v = HYGIENE_VALUES[range]
  return [
    {
      id: 'net-hygiene-production',
      leftLabel: 'Net hygiene production',
      rightLabel: `Goal ${v.netGoal}`,
      value: v.netHygieneProduction,
      footer: footerFor(range, '6%', '$800'),
      footerTrend: 'up',
    },
    {
      id: 'production-per-visit',
      leftLabel: 'Production per hygiene visit',
      rightLabel: `Goal ${v.visitGoal}`,
      value: v.productionPerVisit,
      footer: footerFor(range, '12%', '$1,200'),
      footerTrend: 'up',
    },
    {
      id: 'ar-over-90',
      leftLabel: 'AR over 90-day',
      rightLabel: `Goal ${v.ar90Goal}`,
      value: v.arOver90,
      footer: '$1.2K over 90 days',
    },
  ]
}
